import React from 'react';
import propTypes from 'prop-types';
import { Form } from 'shared-ui';
import { isDefined, isValidDate, ObjectAccessor, formatDate } from '@dateam/ark';
import { concatClassName } from '@dateam/ark-react';
import { plotDetailsPropTypes } from 'data/plot';
import { validate as validator, ValidationError } from 'data/observation';
import { SYMPTOM_VALUES } from 'data/observation/observation33';
import { ObservationProps } from './types';
import { ObservationErrors } from './components';
import ObservationField from './ObservationField';
import ObservationItem from './ObservationItem';
import styles from './Observation.module.scss';

const observationKey = 'observation33';

type Props = ObservationProps<App.Observation33>;

const Observation33: React.FC<Props> = ({
    data,
    plot,
    ignorable,
    className,
    onChange
}: Props) => {
    const [validationErrors, setValidationErrors] = React.useState<Record<string, ValidationError>>({});
    const internalData = React.useRef(data);

    React.useEffect(() => {
        internalData.current = data;
    }, [data]);

    React.useEffect(() => {
        if (data.symptoms === undefined &&
            data.count === undefined) return;

        setValidationErrors(validator(data, plot));
    }, [plot, data]);

    const symptoms = React.useMemo(() => {
        if (data.ignored) return undefined;

        return data.symptoms ?? undefined;
    }, [data]);

    const count = React.useMemo(() => {
        if (data.ignored) return undefined;

        return data.count ?? undefined;
    }, [data]);

    const previousValue = React.useMemo(() => {
        if (isDefined(internalData.current.previous)) {
            return (
                <>
                    {isValidDate(internalData.current.previous.updatedOn) && (
                        <div className={styles['previousDate']}>{formatDate(internalData.current.previous.updatedOn)}</div>
                    )}
                    <div className={styles['previous']}>Symptômes: {internalData.current.previous.symptoms?.value ?? '-'}</div>
                    <div className={styles['previous']}>Si fort: {internalData.current.previous.count?.value ?? '-'}</div>
                </>
            );
        }

        return (<div>Aucune valeur disponible</div>);
    }, [internalData.current]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleIgnoreObservation = React.useCallback(() => {
        const newData: App.Observation33 = {
            ...internalData.current,
            symptoms: null,
            count: null,
            ignored: !internalData.current.ignored
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    const handleSymptomsChange = React.useCallback((newValue: string | undefined) => {
        const newData: App.Observation33 = {
            ...internalData.current,
            symptoms: newValue ?? null
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    const handleCountChange = React.useCallback((newValue: number | undefined) => {
        const newData: App.Observation33 = {
            ...internalData.current,
            count: newValue ?? null
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    const handleFocus = React.useCallback((field: ObjectAccessor<Omit<App.Observation33, 'previous'>>) => {
        if (internalData.current[field] !== undefined) return;

        const newData: App.Observation33 = {
            ...internalData.current,
            [field]: null
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    return (
        <ObservationItem
            data={data}
            ignorable={ignorable}
            onIgnore={handleIgnoreObservation}
            previous={previousValue}
            label="Cicadelle Verte"
        >
            <div className="row">
                <ObservationField
                    id={`${observationKey}-symptoms`}
                    label="Symptômes"
                    className={concatClassName(styles[`${observationKey}-symptoms`], 'col-4')}
                >
                    <Form.Select
                        selected={symptoms}
                        onChange={handleSymptomsChange}
                        state={validationErrors['symptoms']?.type}
                        onFocus={() => handleFocus('symptoms')}
                        required={!data.ignored}
                        readOnly={data.ignored}
                    >
                        <Form.Select.Option value={null} />
                        {SYMPTOM_VALUES.map(option => (
                            <Form.Select.Option key={option} value={option}>
                                {option}
                            </Form.Select.Option>
                        ))}
                    </Form.Select>
                </ObservationField>
                <ObservationField
                    id={`${observationKey}-count`}
                    label="Si fort"
                    offLabel="/25 feuilles"
                    className={concatClassName(styles[`${observationKey}-count`], 'col-4')}
                >
                    <Form.Number
                        min={0}
                        max={25}
                        value={count}
                        onChange={handleCountChange}
                        state={validationErrors['count']?.type}
                        onFocus={data.symptoms === 'M' ? () => handleFocus('count') : undefined}
                        required={!data.ignored && data.symptoms === 'M'}
                        readOnly={data.ignored}
                    />
                </ObservationField>
            </div>
            <ObservationErrors value={validationErrors} observationKey={observationKey} />
        </ObservationItem>
    );
};

Observation33.propTypes = {
    data: propTypes.any.isRequired,
    plot: plotDetailsPropTypes.isRequired,
    className: propTypes.string,
    onChange: propTypes.func
};

Observation33.defaultProps = {
    className: undefined,
    onChange: undefined
};

export default Observation33;
